/** The Add-user email rule, spelled exactly as the API spells it (MS-996
 * review): `normalize_end_user_email` (`email.strip().lower()`,
 * repositories/end_user_repository.py) then `_is_plausible_email`
 * (api/v1/end_users.py). Python's `str.strip()`/`str.isspace()` and JS's
 * `trim()`/`\s` disagree on whitespace: Python counts U+001C–U+001F and U+0085
 * and JS does not, while JS counts U+FEFF and Python does not. So neither
 * `trim()` nor `/\s/` is used here: an address the form accepts is one the API
 * accepts, and the other way round. */

/** Every character Python's `str.isspace()` is true for (Unicode 15). */
const PY_WHITESPACE = new Set(
  [
    0x09, 0x0a, 0x0b, 0x0c, 0x0d, 0x1c, 0x1d, 0x1e, 0x1f, 0x20, 0x85, 0xa0, 0x1680, 0x2000, 0x2001, 0x2002, 0x2003,
    0x2004, 0x2005, 0x2006, 0x2007, 0x2008, 0x2009, 0x200a, 0x2028, 0x2029, 0x202f, 0x205f, 0x3000,
  ].map((code) => String.fromCodePoint(code))
)

/** Python's `ch.isspace()` for one character. */
export const isPyWhitespace = (ch: string): boolean => PY_WHITESPACE.has(ch)

/** Python's `str.strip()` with no argument. Every whitespace character is in
 * the BMP, so stepping by UTF-16 code unit is exact. */
export function pyStrip(value: string): string {
  let start = 0
  let end = value.length
  while (start < end && isPyWhitespace(value[start])) start += 1
  while (end > start && isPyWhitespace(value[end - 1])) end -= 1
  return value.slice(start, end)
}

/** `normalize_end_user_email`: stripped, then lower-cased. */
export const normalizeEmail = (value: string): string => pyStrip(value).toLowerCase()

/** `_is_plausible_email`, the prototype's /^[^\s@]+@[^\s@]+\.[^\s@]+$/: no
 * whitespace, one '@', a local part, and a '.' with a character on each side
 * of it in the domain. Written without a regex, which backtracks
 * super-linearly on a long dotless input (Sonar typescript:S5852). */
export function isPlausibleEmail(value: string): boolean {
  if ([...value].some(isPyWhitespace)) return false
  const parts = value.split('@')
  if (parts.length !== 2 || !parts[0]) return false
  // By code point, as Python slices.
  return [...parts[1]].slice(1, -1).includes('.')
}

/** `is_portal_email` (billing_portal/emails.py, MS-997 A3), the Settings ›
 * Access rule: `isPlausibleEmail`, ASCII only, and no `"`, `\` or control
 * character. ASCII only so that lower-casing cannot turn a look-alike into a
 * plain letter (U+212A KELVIN SIGN lower-cases to "k"); an internationalised
 * domain is accepted in its punycode (`xn--`) form. Apply it to the stripped
 * input before lower-casing as well as after, as the API does. User Limits
 * keeps `isPlausibleEmail`. */
export function isPortalEmail(value: string): boolean {
  for (const ch of value) {
    const code = ch.codePointAt(0) ?? 0
    if (code > 0x7e || code < 0x20 || ch === '"' || ch === '\\') return false
  }
  return isPlausibleEmail(value)
}
